<script lang="ts" setup>
import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'
import { addHours, parse, format } from 'date-fns'
import { getErrorMessage } from '~/utils/errors'

// Config
const appConfig = useAppConfig()

// Emits
const emit = defineEmits(['search-map', 'zoom-map', 'toggle-favorite'])

// Router
const router = useRouter()

// Idioma
const { locale, t } = useI18n()
const browserLang = (typeof navigator !== 'undefined' && navigator.language) || locale.value
const { isPackFirstFlow } = useCheckoutFlowExperiment()
const { isRealPriceDisplay } = useRealPriceExperiment()

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)

// Favorites Store
const favoritesStore = useFavoritesStore()
const { isFavorite } = favoritesStore

// Ubicacion
const selectedDestStore = useDestinationStore()
const { selectedDestination, destinationDisplayMode } = storeToRefs(selectedDestStore)

const showCustomZonePointer = computed(() => {
  const d = selectedDestination.value
  if (!d?.location) return false
  if (destinationDisplayMode.value === 'map_point') return true
  return !d.element_id
})

// Props
const props = defineProps<{
  boundHoteles?: HotelPageHotel[]
  lat?: number
  lon?: number
  allHotels?: HotelPageHotel[]
  hoverHotel?: number
  urlParams?: string
  pending?: boolean
  selectedPack?: SearchPackHours
}>()

const mapRef = ref<MapRef | null>(null)
const zoomMapa = ref<number>(12)

const latitudesAndLongitudes = computed((): Array<[number, number]> => { // centrar mapa dependiendo de los markers de los hoteles
  return props.boundHoteles?.map((hotel) => {
    return [Number(hotel.latitude), Number(hotel.longitude)]
  }) || []
})

watch(() => props.pending, (newPending) => {
  if (!newPending) {
    if (latitudesAndLongitudes.value.length > 1) {
      boundMap(latitudesAndLongitudes.value)
    } else if (latitudesAndLongitudes.value.length === 1) {
      moverMapa({ lat: latitudesAndLongitudes.value[0]?.[0] ?? 0, lng: latitudesAndLongitudes.value[0]?.[1] ?? 0 })
    }
  }
})

const boundMap = (arrayLatitudes: Array<[number, number]>) => {
  if (arrayLatitudes.length === 0) return
  mapRef.value?.leafletObject?.fitBounds(arrayLatitudes)
}

const visibleZona = ref(false)
const isUserMoving = ref(false)

const moverMapa = (center: { lat: number, lng: number }) => {
  centerLonLat.value = center
  if (isUserMoving.value) {
    visibleZona.value = true // mostrar botón de volver a la zona
  }
}

const centerLonLat = ref<{ lat: number, lng: number }>({ lat: props?.lat ?? 0, lng: props?.lon ?? 0 })

const actualizarZoom = (zoom: number) => {
  zoomMapa.value = zoom
  // Solo marcar como movimiento de usuario si no estamos en pending
  if (!props.pending) {
    isUserMoving.value = true
  }
}

const deviceStore = useDeviceStore();
const isMobile = deviceStore.isMobile;
const isGoogle = ref(true)

const isSearch = router.currentRoute.value.path.includes('search')

// Sincronizar favoritos iniciales cuando se cargan los hoteles
watch(() => props.allHotels, (newHotels) => {
  if (newHotels) {
    favoritesStore.syncHotelFavorites(newHotels)
  }
}, { immediate: true })

const buscarEnZona = () => {
  emit('search-map', {
    center: { lat: centerLonLat.value.lat, lng: centerLonLat.value.lng },
    zoom: zoomMapa.value
  })
  visibleZona.value = false
}

watch([() => props.lat, () => props.lon], ([newLat, newLon]) => {
  if (newLat === undefined || newLon === undefined) return
  const sameCenter =
    Math.abs(centerLonLat.value.lat - newLat) < 1e-6
    && Math.abs(centerLonLat.value.lng - newLon) < 1e-6
  if (sameCenter) return

  centerLonLat.value = { lat: newLat, lng: newLon }
  zoomMapa.value = 12
  isUserMoving.value = false
  visibleZona.value = false

  if (mapRef.value?.leafletObject) {
    mapRef.value.leafletObject.setView([newLat, newLon], 12)
  }
})

const today = ref(formatDateApi(new Date()))
const dispo = ref(false)
const showSkeleton = ref(false)
const searchAvailabilty = async (hotel_id: number) => {
  showSkeleton.value = true

  try {
    const dispoData = await $fetch<HotelDaysAvailabilityResponse>(`${useBaseUrl()}/hotel-days-availability`, {
      headers: {
        "Content-Type": "application/json",
      },
      query: {
        date: today.value,
        hotel_id,
        channel_id: appConfig.channel_id,
        currency: currency.value,
        months: 12
      }
    })
    dispo.value = !objIsEmpty(dispoData?.data?.pack_hour ?? {})
  } catch (error: unknown) {
    logError(error)
  } finally {
    showSkeleton.value = false
  }
}

const selectedHotel = ref<HotelPageHotel | null>(null)
const openedPopupId = ref<number | null>(null)

onBeforeUnmount(() => {
  if (mapRef.value?.leafletObject) {
    const map = mapRef.value.leafletObject

    try {
      map.off?.()
      map.eachLayer((layer: LeafletLayerLike) => {
        if (layer.remove) {
          layer.remove()
        }
      })
      map.stop?.()
    } catch (error: unknown) {
      console.warn('Error cleaning up Leaflet map:', getErrorMessage(error))
    }

    mapRef.value = null
  }
})

const activeIndex = ref(0)
const hoverCarousel = ref(false)
function onSelect(index: number) {
  activeIndex.value = index
}

const openDispoModal = ref(false)
const dispoHotelData = ref<HotelPageHotel | null>(null)

const sumarHoras = (hora: string, cantidad: number): string => {
  const is12h = /AM|PM/i.test(hora)
  let date
  if (is12h) {
    date = parse(hora, 'hh:mm a', new Date())
  } else {
    date = parse(hora, 'HH:mm', new Date())
  }
  const nueva = addHours(date, cantidad)
  return is12h ? format(nueva, 'hh:mm a') : format(nueva, 'HH:mm')
}

const PACK_HOURS = [3, 6, 24] as const

const getArrayHoras = (hotel: HotelPageHotel | Parameters<typeof getHorasHotel>[0]): Record<string, string[]> => {
  return getHorasHotel(hotel, browserLang)
}

const getAvailableHours = (hotel: HotelPageHotel | Parameters<typeof getHorasHotel>[0]) => {
  const horas = getArrayHoras(hotel)
  return PACK_HOURS.filter(hour => horas?.[hour]?.length)
}

function getHotelPromos(hotel: HotelPageHotel): Promo[] {
  const seen = new Set<string>()
  return (hotel?.promo ?? []).filter((p: Promo) => {
    if (!p.short_description || seen.has(p.short_description)) return false
    seen.add(p.short_description)
    return true
  })
}

function hasMultiplePromos(hotel: HotelPageHotel): boolean {
  return getHotelPromos(hotel).length > 1
}

function getFirstUniquePromo(hotel: HotelPageHotel): string | null {
  return getHotelPromos(hotel)[0]?.short_description ?? null
}

function getMultiplePromosBadgeLabel(hotel: HotelPageHotel): string {
  return t('searchCard.promos.multiple', { count: getHotelPromos(hotel).length })
}

// Los filtros (pack) solo aplican a los hoteles de resultado (boundHoteles), no a los alternativos.
const resultHotelIds = computed(() => new Set((props.boundHoteles ?? []).map(h => Number(h.id))))

function packForHotel(hotel: HotelPageHotel): SearchPackHours | undefined {
  return resultHotelIds.value.has(Number(hotel?.id)) ? props.selectedPack : undefined
}

function getMinPriceLabel(hotel: HotelPageHotel): string {
  const pack = packForHotel(hotel)
  if (pack) {
    const packLabel = precioLabelForPack(hotel, pack, isRealPriceDisplay.value)
    if (packLabel) return packLabel
  }
  const arrayHoras = getArrayHoras(hotel)
  const packs = hotel?.pack_min_price ?? {}
  let bestPrice = Infinity
  let bestLabel = ''
  for (const p of PACK_HOURS) {
    if (!arrayHoras?.[p]?.length) continue
    const { price, label } = packSlotDisplayPrice(packs[p], isRealPriceDisplay.value)
    if (typeof price !== 'number' || Number.isNaN(price)) continue
    if (price < bestPrice) {
      bestPrice = price
      bestLabel = label
    }
  }
  return bestLabel || primerPrecioLabel(hotel, isRealPriceDisplay.value)
}

function getDisplayPricePrefix(hotel: HotelPageHotel): string {
  const pack = packForHotel(hotel)
  if (pack) {
    return `${pack}h ${t('buttons.desde').toLowerCase()}`
  }
  return t('buttons.desde').toLowerCase()
}

function getMarkerPriceLabel(hotel: HotelPageHotel): string {
  return precioLabelForPack(hotel, packForHotel(hotel), isRealPriceDisplay.value)
    || primerPrecioLabel(hotel, isRealPriceDisplay.value)
}

function packMinPriceLabel(hotel: HotelPageHotel | null | undefined, pack: SearchPackHours): string {
  return packSlotDisplayPrice(hotel?.pack_min_price?.[pack], isRealPriceDisplay.value).label
}

function getHotelEnjoymentWindowLabelForHotel(hotel: HotelPageHotel): string {
  const allDay = t('roomCard.packWindow.allDay')
  return getPackAvailabilityWindowLabel(
    packForHotel(hotel),
    hotel?.checkin_times ?? {},
    getAvailableHours(hotel),
    browserLang,
    allDay,
    hotel?.rooms as Record<string, {
      checkin_times?: Record<string, Record<string, boolean | unknown>>
      promo?: { type?: number }
    }> | undefined,
    hotel?.promo as Array<{ type?: number }> | undefined
  )
}

function getMobileEnjoymentWindowBadgeUi(label: string) {
  const mayWrap = /AM|PM/i.test(label)
  if (mayWrap) {
    return {
      base: 'h-auto w-full max-w-full justify-center whitespace-normal',
      label: 'whitespace-normal wrap-break-word leading-snug text-center'
    }
  }
  return {
    base: 'h-auto w-fit max-w-full justify-center whitespace-nowrap',
    label: 'whitespace-nowrap leading-tight'
  }
}

function getAvailablePacksBadgeLabel(hotel: HotelPageHotel): string {
  const hours = getAvailableHours(hotel)
  if (!hours.length) return ''
  if (hours.length === 1) {
    return t('searchCard.availablePacks.one', { pack: hours[0] })
  }
  const packs = hours.map(pack => `${pack}h`).join(' · ')
  return t('searchCard.availablePacks.many', { packs })
}

function buildHotelUrl(hotel: HotelPageHotel, options?: { pack?: number, hora?: string, scrollTo?: string }) {
  const slug = hotel.slug || slugify(hotel.name || 'hotel')
  const params = new URLSearchParams()
  const baseQuery = props.urlParams ?? ''
  if (baseQuery) {
    for (const [key, value] of new URLSearchParams(baseQuery)) {
      params.set(key, value)
    }
  }
  const pack = options?.pack ?? props.selectedPack
  if (pack) params.set('pack', String(pack))
  if (options?.hora) params.set('hora', options.hora)
  if (options?.scrollTo) params.set('scrollTo', options.scrollTo)
  const query = params.toString()
  return `/${locale.value}/${slug}-${hotel.id}.html${query ? `?${query}` : ''}`
}

function hotelNavUrl(hotel: HotelPageHotel) {
  return buildHotelUrl(hotel, {
    scrollTo: isPackFirstFlow.value ? 'seccion_habitaciones' : undefined
  })
}

const openPromosModal = ref(false)
const promosHotelData = ref<HotelPageHotel | null>(null)

function openHotelPromosModal(hotel: HotelPageHotel) {
  promosHotelData.value = hotel
  openPromosModal.value = true
}

function getMinRoomsAvailable(hotel: HotelPageHotel): number {
  const rooms = hotel?.rooms ?? {}
  let total = 0
  let found = false
  for (const roomId of Object.keys(rooms)) {
    const packs = (rooms as Record<string, { packs?: Record<string, { num_rooms_available?: number }> }>)[roomId]?.packs ?? {}
    let roomMin = Infinity
    for (const packKey of ['3', '6', '24']) {
      const n = packs[packKey]?.num_rooms_available
      if (typeof n === 'number' && n < roomMin) roomMin = n
    }
    if (roomMin !== Infinity) {
      total += roomMin
      found = true
    }
  }
  return found ? total : 0
}

function getShowUrgencyBadge(hotel: HotelPageHotel): boolean {
  const n = getMinRoomsAvailable(hotel)
  return n > 0 && n < 2
}

function getShowPackFirstPromoSlot(hotel: HotelPageHotel): boolean {
  return getHotelPromos(hotel).length > 0 || getShowUrgencyBadge(hotel)
}

</script>
<template>

  <!-- Botón buscar en esta zona -->
  <transition enter-active-class="transition-opacity duration-300" enter-from-class="opacity-0"
    enter-to-class="opacity-100" leave-active-class="transition-opacity duration-300" leave-from-class="opacity-100"
    leave-to-class="opacity-0">
    <div v-if="(visibleZona && isSearch) || pending" class="absolute top-4 left-1/2 -translate-x-1/2 z-20">
      <UButton size="xs" color="neutral" variant="subtle" class="shadow-md" @click="buscarEnZona"
        :icon="pending ? 'i-svg-spinners-3-dots-scale' : undefined"
        :label="pending ? undefined : $t('buttons.buscarZona')" :ui="{
          leadingIcon: 'size-6 mx-3 text-toned'
        }" />
    </div>
  </transition>
  <l-map class="w-full h-full rounded-3xl z-0" ref="mapRef" :max-zoom="18" :zoom="zoomMapa"
    @click="selectedHotel = null" @movestart="selectedHotel = null"
    :options="{ zoomControl: false, attributionControl: false }" :center="[centerLonLat.lat, centerLonLat.lng]"
    @update:center="moverMapa" @update:zoom="actualizarZoom"
    @moveend="() => { if (!props.pending) isUserMoving = true }" :useGlobalLeaflet="true">
    <l-tile-layer
      :url="isGoogle ? `http://{s}.google.com/vt/lyrs=m&hl=${locale}&x={x}&y={y}&z={z}` : `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png`"
      :minZoom="3" :maxZoom="18" :subdomains="['mt0', 'mt1', 'mt2', 'mt3']" />
    <l-marker v-if="showCustomZonePointer"
      :lat-lng="[
        Number(selectedDestination?.location?.split(',')[0] ?? 0),
        Number(selectedDestination?.location?.split(',')[1] ?? 0)
      ]">
      <l-icon>
        <span class="relative flex size-3">
          <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4285F4] opacity-50"></span>
          <span class="relative inline-flex size-3 rounded-full bg-[#4285F4] border border-white"></span>
        </span>
      </l-icon>
    </l-marker>
    <l-marker-cluster-group :showCoverageOnHover="false" :max-zoom="16" :disable-clustering-at-zoom="13"
      :max-cluster-radius="20" :spiderfy-on-max-zoom="false" :zoom-to-bounds-on-click="true"
      :remove-outside-visible-bounds="true">
      <template v-for="(hotel, markerIndex) in allHotels" :key="`marker-${hotel?.id ?? markerIndex}-${markerIndex}`">
        <l-marker v-if="hotel?.id && !pending" :lat-lng="[Number(hotel?.latitude), Number(hotel?.longitude)]"
          @click="() => {
            selectedHotel = hotel;
            if (hotel.id != null && objIsEmpty(hotel.pack_min_price) && !isSearch) searchAvailabilty(hotel.id);
          }" @popupopen="openedPopupId = hotel.id" @popupclose="openedPopupId = null">
          <l-icon>
            <div v-if="!objIsEmpty(hotel?.pack_min_price)"
              :class="`${hotel?.id === hoverHotel || selectedHotel?.id === hotel.id ? 'bg-byhours-gradient' : 'bg-neutral-800'}`"
              class="hover:scale-105 duration-100 -ml-5 -mt-1 font-semibold p-1 px-2 rounded-full text-sm text-white w-fit text-nowrap flex items-center justify-center drop-shadow-md">
              <div class="flex items-center gap-1">
                <UIcon v-if="isFavorite(hotel?.id)" name="i-heroicons-heart-solid" class="text-xs text-error" />
                {{ getMarkerPriceLabel(hotel) }}
              </div>
            </div>
            <div v-else-if="!isSearch"
              class="hover:scale-105 duration-100 bg-neutral-100 border border-neutral-300 drop-shadow-lg rounded-full p-1 px-3 -ml-4 text-black text-xs font-semibold w-fit text-nowrap flex items-center justify-center cursor-pointer">
              <UIcon name="i-lucide-building-2" class="text-lg" />
            </div>

          </l-icon>

          <!-- Popup solo en Desktop -->
          <l-popup v-if="!isMobile" class="w-60 sm:w-72" :options="{ closeButton: false }">
            <template v-if="openedPopupId === hotel.id">
              <div class="relative">
                <UCarousel
                  :arrows="hoverCarousel"
                  v-slot="{ item }"
                  :items="hotel?.images"
                  :class="`h-full`"
                  :next="{ size: 'xs', variant: 'soft' }"
                  :prev="{ size: 'xs', variant: 'soft' }"
                  :ui="{
                    item: 'basis-full h-full',
                    container: 'h-full',
                    viewport: 'aspect-16/9 w-full h-full rounded-2xl',
                    next: 'sm:end-2',
                    prev: 'sm:start-2'
                  }"
                  @mouseover="hoverCarousel = true"
                  @mouseleave="hoverCarousel = false"
                  @select="onSelect"
                >
                  <NuxtImg provider="byhours" format="webp" width="300" height="200"
                    :src="generateImgUrl(String(item ?? ''))"
                    @click="navigateTo(hotelNavUrl(hotel), { open: { target: '_blank' } })"
                    :alt="`Imagen hotel`" class="w-full h-full object-cover cursor-pointer rounded-2xl"
                    draggable="false" loading="lazy" />
                </UCarousel>

                <!-- Promo/urgencia (test) o horas disponibles (control) -->
                <div class="absolute top-0 left-0 z-10 max-w-[calc(100%-3rem)] p-2">
                  <div
                    v-if="isPackFirstFlow && getShowPackFirstPromoSlot(hotel)"
                    class="flex max-w-full flex-wrap items-center gap-1.5"
                  >
                    <UBadge
                      v-if="hasMultiplePromos(hotel)"
                      :aria-label="getMultiplePromosBadgeLabel(hotel)"
                      color="secondary"
                      variant="soft"
                      size="md"
                      icon="i-lucide-tag"
                      :label="getMultiplePromosBadgeLabel(hotel)"
                      class="bg-cyan-50 cursor-pointer"
                      @click.stop.prevent="openHotelPromosModal(hotel)"
                    />
                    <UBadge
                      v-else-if="getFirstUniquePromo(hotel)"
                      :aria-label="$t('strapi.searchPage.filtroPromo')"
                      color="secondary"
                      variant="soft"
                      size="md"
                      :label="getFirstUniquePromo(hotel)!"
                      class="bg-cyan-50"
                    />
                    <UBadge
                      v-else-if="getShowUrgencyBadge(hotel)"
                      color="primary"
                      variant="soft"
                      size="md"
                      class="bg-orange-50"
                      :label="getMinRoomsAvailable(hotel) === 1 ? t('searchCard.urgency.oneRoom') : t('searchCard.urgency.moreRooms', { count: getMinRoomsAvailable(hotel) })"
                    />
                  </div>

                  <UButton
                    v-else-if="!isPackFirstFlow && getAvailableHours(hotel).length"
                    :label="$t('buttons.dispo')"
                    color="neutral"
                    variant="soft"
                    size="xs"
                    icon="i-lucide-clock"
                    class="shrink-0"
                    :ui="{ base: 'drop-shadow-md' }"
                    @click.stop.prevent="() => { dispoHotelData = hotel; openDispoModal = true }"
                  />
                </div>

                <!-- Botón de favorito -->
                <div class="absolute top-0 right-0 p-2 z-10">
                  <UButton :aria-label="$t('labels.aria.favorito')"
                    :icon="isFavorite(hotel?.id) ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" color="neutral"
                    size="xs" variant="soft" class="hover:scale-105 transition-all duration-300" :ui="{
                      base: 'drop-shadow-md',
                      leadingIcon: isFavorite(hotel?.id) ? 'text-error' : 'text-neutral-900'
                    }" @click.stop.prevent="emit('toggle-favorite', hotel.id)" />
                </div>
              </div>

              <NuxtLink
                :to="hotelNavUrl(hotel)"
                target="_blank"
                class="w-full flex flex-col p-3 justify-between bg-white shadow-[0px_0px_10px_0_rgba(0,0,0,0.05)] mt-2 rounded-2xl drop-shadow-md hover:drop-shadow-lg duration-300 transition-all"
                :aria-label="hotel?.name">
                <UBadge
                  v-if="isPackFirstFlow && !objIsEmpty(hotel?.pack_min_price) && getAvailablePacksBadgeLabel(hotel)"
                  color="success"
                  variant="soft"
                  size="md"
                  icon="i-lucide-bed-double"
                  :label="getAvailablePacksBadgeLabel(hotel)"
                  class="mb-2 w-full max-w-full justify-center truncate"
                />

                <div class="flex flex-col min-w-0">
                  <div class="flex shrink-0 text-neutral-900 mb-0.5">
                    <UIcon :key="index" v-for="index in splitEstrellas(hotel?.stars?.abbr).countEstrellas"
                      name="i-heroicons-star-solid" class="text-xs text-neutral-800" />
                    <span class="text-xs font-bold">{{ splitEstrellas(hotel?.stars?.abbr).rightString }}</span>
                  </div>
                  <p class="text-sm sm:text-base font-semibold my-0 text-neutral-800 truncate">{{ hotel?.name }}</p>
                </div>

                <!-- Pack-first -->
                <div v-if="isPackFirstFlow && !objIsEmpty(hotel?.pack_min_price)" class="mt-1 grid grid-cols-2 gap-2 min-w-0 items-end">
                  <div>
                    <UButton
                      v-if="getHotelEnjoymentWindowLabelForHotel(hotel)"
                      color="neutral"
                      variant="outline"
                      size="sm"
                      :label="getHotelEnjoymentWindowLabelForHotel(hotel)"
                    />
                  </div>
                  <div v-if="getMinPriceLabel(hotel)" class="flex flex-col items-end justify-end min-w-0">
                    <span class="text-sm text-neutral-500">{{ getDisplayPricePrefix(hotel) }}</span>
                    <span class="text-lg font-semibold leading-tight text-neutral-900 whitespace-nowrap">{{ getMinPriceLabel(hotel) }}</span>
                  </div>
                </div>
                <!-- Control: tabla de precios -->
                <div v-else-if="!objIsEmpty(hotel?.pack_min_price)"
                  class="grid grid-rows-1 grid-flow-col gap-2 mt-1 sm:mt-2"
                  :class="packForHotel(hotel) ? 'grid-cols-1' : 'grid-cols-3'">
                  <template v-if="packForHotel(hotel)">
                    <div v-if="getMinPriceLabel(hotel)">
                      <span class="text-xs sm:text-sm text-neutral-400 uppercase">{{ getDisplayPricePrefix(hotel) }}</span>
                      <div class="text-xs sm:text-base text-neutral-800 font-bold">{{ getMinPriceLabel(hotel) }}</div>
                    </div>
                  </template>
                  <template v-else>
                    <div v-if="packMinPriceLabel(hotel, 3)">
                      <span class="text-xs sm:text-sm text-neutral-400 uppercase">3h</span>
                      <div class="text-xs sm:text-base text-neutral-800 font-bold">{{
                        packMinPriceLabel(hotel, 3) }}</div>
                    </div>
                    <div v-if="packMinPriceLabel(hotel, 6)">
                      <span class="text-xs sm:text-sm text-neutral-400 uppercase">6h</span>
                      <div class="text-xs sm:text-base text-neutral-800 font-bold">{{
                        packMinPriceLabel(hotel, 6) }}</div>
                    </div>
                    <div v-if="packMinPriceLabel(hotel, 24)">
                      <span class="text-xs sm:text-sm text-neutral-400 uppercase">24h</span>
                      <div class="text-xs sm:text-base text-neutral-800 font-bold">{{
                        packMinPriceLabel(hotel, 24) }}</div>
                    </div>
                  </template>
                </div>
                <div v-else class="mt-2 text-center">
                  <UBadge v-if="!dispo" color="neutral" variant="subtle" size="md">{{ $t('buttons.noDispo') }}</UBadge>
                  <UBadge v-else color="info" variant="subtle" size="md">{{ $t('buttons.dispoOtrosDias') }}</UBadge>
                </div>
              </NuxtLink>
            </template>
          </l-popup>
        </l-marker>
      </template>
    </l-marker-cluster-group>
  </l-map>

  <!-- Carta de Hotel Horizontal (Fixed Bottom) -->
  <transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100" leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="translate-y-0 opacity-100" leave-to-class="translate-y-full opacity-0">
    <div v-if="isMobile && selectedHotel"
      :class="[
        'absolute bottom-2 left-1/2 -translate-x-1/2 z-20 w-[95%] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-row',
        isPackFirstFlow ? 'min-h-28 h-auto' : 'h-28'
      ]">

      <!-- Imagen -->
      <div class="w-1/3 min-h-28 relative shrink-0 self-stretch">
        <NuxtImg provider="byhours" format="webp" width="150" height="150"
          :src="generateImgUrl(String(selectedHotel.images?.[0] ?? ''))"
          @click="navigateTo(hotelNavUrl(selectedHotel), { open: { target: '_blank' } })"
          :alt="selectedHotel.name" class="w-full h-full object-cover cursor-pointer" draggable="false"
          loading="lazy" />

        <!-- Botón favorito -->
        <div class="absolute top-1 left-1">
          <UButton :aria-label="$t('labels.aria.favorito')"
            :icon="isFavorite(selectedHotel.id ?? 0) ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" color="neutral"
            size="xs" variant="soft" class="rounded-full shadow-sm" :ui="{
              leadingIcon: isFavorite(selectedHotel.id ?? 0) ? 'text-error' : 'text-neutral-900'
            }" @click.stop.prevent="emit('toggle-favorite', selectedHotel.id ?? 0)" />
        </div>
      </div>

      <!-- Info -->
      <div
        class="w-2/3 p-3 flex flex-col min-w-0 relative"
        :class="isPackFirstFlow ? 'justify-between' : 'justify-between gap-1'"
        @click.stop.prevent="navigateTo(hotelNavUrl(selectedHotel), { open: { target: '_blank' } })"
      >
        <div
          class="flex flex-col min-w-0"
          :class="isPackFirstFlow ? 'flex-1' : 'pr-10'"
        >
          <div
            v-if="isPackFirstFlow"
            class="flex items-center gap-1 -mt-2 -mr-2 mb-0.5 justify-between"
          >
            <UBadge
              v-if="!objIsEmpty(selectedHotel?.pack_min_price) && getAvailablePacksBadgeLabel(selectedHotel)"
              color="success"
              variant="soft"
              size="sm"
              icon="i-lucide-bed-double"
              :label="getAvailablePacksBadgeLabel(selectedHotel)"
              class="min-w-0 flex-1 justify-center truncate"
            />
            <UButton
              icon="i-heroicons-x-mark"
              color="neutral"
              variant="ghost"
              size="md"
              class="rounded-full shrink-0"
              @click.stop.prevent="() => { selectedHotel = null }"
            />
          </div>

          <UButton
            v-else
            icon="i-heroicons-x-mark"
            color="neutral"
            variant="ghost"
            size="md"
            class="absolute top-1 right-1 z-10 rounded-full"
            @click.stop.prevent="() => { selectedHotel = null }"
          />

          <div class="flex items-center gap-0.5 text-neutral-800 shrink-0 mb-0.5">
            <UIcon v-for="index in splitEstrellas(selectedHotel?.stars?.abbr).countEstrellas" :key="index"
              name="i-heroicons-star-solid" class="text-xs" />
            <span class="text-xs font-bold ml-1">{{ splitEstrellas(selectedHotel?.stars?.abbr).rightString
            }}</span>
          </div>
          <div class="text-sm font-bold text-neutral-900 truncate hover:text-primary transition-colors">
            {{ selectedHotel.name }}
          </div>
        </div>

        <!-- Pack-first -->
        <div v-if="isPackFirstFlow && !objIsEmpty(selectedHotel?.pack_min_price)" class="mt-1.5 grid grid-cols-[minmax(0,1fr)_auto] gap-2 min-w-0 items-end">
          <UBadge
            v-if="getHotelEnjoymentWindowLabelForHotel(selectedHotel)"
            color="neutral"
            variant="outline"
            :label="getHotelEnjoymentWindowLabelForHotel(selectedHotel)"
            class="min-w-0 max-w-full justify-self-start"
            :ui="getMobileEnjoymentWindowBadgeUi(getHotelEnjoymentWindowLabelForHotel(selectedHotel))"
          />
          <div v-if="getMinPriceLabel(selectedHotel)" class="flex shrink-0 flex-col items-end justify-end pl-1">
            <span class="text-xs text-neutral-500 leading-none">{{ getDisplayPricePrefix(selectedHotel) }}</span>
            <span class="text-base font-bold text-neutral-900 text-right wrap-break-word leading-tight">{{ getMinPriceLabel(selectedHotel) }}</span>
          </div>
        </div>
        <!-- Control: precios por pack -->
        <div v-else-if="!objIsEmpty(selectedHotel?.pack_min_price)" class="flex gap-3 items-end pr-10">
          <template v-if="packForHotel(selectedHotel)">
            <div v-if="getMinPriceLabel(selectedHotel)" class="flex flex-col leading-tight">
              <span class="text-xs text-neutral-400 uppercase">{{ getDisplayPricePrefix(selectedHotel) }}</span>
              <span class="text-sm/4 font-bold text-neutral-900">{{ getMinPriceLabel(selectedHotel) }}</span>
            </div>
          </template>
          <template v-else>
            <div v-if="packMinPriceLabel(selectedHotel, 3)" class="flex flex-col leading-tight">
              <span class="text-xs text-neutral-400 uppercase">3h</span>
              <span class="text-sm/4 font-bold text-neutral-900">{{ packMinPriceLabel(selectedHotel, 3)
              }}</span>
            </div>
            <div v-if="packMinPriceLabel(selectedHotel, 6)" class="flex flex-col leading-tight">
              <span class="text-xs text-neutral-400 uppercase">6h</span>
              <span class="text-sm/4 font-bold text-neutral-900">{{ packMinPriceLabel(selectedHotel, 6)
              }}</span>
            </div>
            <div v-if="packMinPriceLabel(selectedHotel, 24)" class="flex flex-col leading-tight">
              <span class="text-xs text-neutral-400 uppercase">24h</span>
              <span class="text-sm/4 font-bold text-neutral-900">{{ packMinPriceLabel(selectedHotel, 24)
              }}</span>
            </div>
          </template>
        </div>
        <div v-else class="flex items-center gap-1">
          <div v-if="showSkeleton" class="h-8 w-16 bg-neutral-100 animate-pulse rounded-full"></div>
          <UBadge v-else :color="dispo ? 'info' : 'neutral'" variant="subtle">
            {{ dispo ? $t('buttons.dispoOtrosDias') : $t('buttons.noDispo') }}
          </UBadge>
        </div>
      </div>
    </div>
  </transition>

  <!-- Modal promos (pack-first) -->
  <UModal
    v-if="!isMobile"
    v-model:open="openPromosModal"
    :title="$t('searchCard.promos.modalTitle')"
    :ui="{ content: 'max-w-lg max-h-[80vh] overflow-y-auto' }"
  >
    <template #body>
      <ul v-if="promosHotelData" class="flex flex-col gap-4">
        <li v-for="(promo, index) in getHotelPromos(promosHotelData)" :key="index" class="flex flex-col gap-1">
          <p class="text-sm font-semibold">{{ promo.short_description }}</p>
          <p v-if="promo.description" class="text-sm text-neutral-600">{{ promo.description }}</p>
        </li>
      </ul>
    </template>
  </UModal>

  <UDrawer
    v-else
    v-model:open="openPromosModal"
    :title="$t('searchCard.promos.modalTitle')"
  >
    <template #body>
      <ul v-if="promosHotelData" class="flex flex-col gap-4 p-4">
        <li v-for="(promo, index) in getHotelPromos(promosHotelData)" :key="index" class="flex flex-col gap-1">
          <p class="text-sm font-semibold">{{ promo.short_description }}</p>
          <p v-if="promo.description" class="text-sm text-neutral-600">{{ promo.description }}</p>
        </li>
      </ul>
    </template>
  </UDrawer>

  <!-- Modal Disponibilidad Map -->
  <UModal v-model:open="openDispoModal" :ui="{ content: 'max-w-2xl max-h-[80vh] overflow-y-auto' }"
    :title="$t('labels.horasDisponibles')">
    <template #body>
      <div class="text-xs" v-if="dispoHotelData">
        <div v-for="key in getAvailableHours(dispoHotelData)" :key="key">
          <div v-if="getArrayHoras(dispoHotelData)[key]?.length" class="pb-6">
            <div class="font-bold mb-2">{{ key }}h</div>
            <div class="flex flex-wrap gap-2 sm:gap-3">
              <div v-for="item in (getArrayHoras(dispoHotelData)[key] as string[])" :key="`${key}-${item}`">
                <UButton
                  :label="getArrayHoras(dispoHotelData)[key]?.length === 1 ? item : `${item} - ${sumarHoras(item, Number(key))}`"
                  color="neutral" variant="outline" size="xs"                  
                  @click="() => {
                    const h = dispoHotelData
                    if (!h?.id) return
                    navigateTo(`/${locale}/${h.slug || slugify(h.name || 'hotel')}-${h.id}.html?${urlParams ?? ''}&pack=${key}&hora=${item}`, { open: { target: '_blank' } })
                  }" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UModal>

</template>

<style lang="css">
.leaflet-div-icon {
  background-color: transparent;
  border: none;
}

.marker-cluster-medium,
.marker-cluster-small {
  background-color: #1F2937;
  color: #fff;
}

.marker-cluster-medium div,
.marker-cluster-small div {
  background-color: #1F2937;
  font-weight: bold;
}

.leaflet-popup-content-wrapper {
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.leaflet-popup-content-wrapper .leaflet-popup-content {
  margin: 0;
}

.leaflet-popup-content-wrapper .leaflet-popup-content p {
  margin: 0;
}
</style>